import React from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiBook, FiCoffee, FiHeart, FiMapPin, FiCalendar } = FiIcons;

const CampusOutreach = () => {
  const activities = [
    {
      icon: FiUsers,
      title: 'Campus Conversations',
      description: 'Engaging students in authentic conversations about life, faith, and purpose in high-traffic campus areas.',
      frequency: 'Daily during lunch hours'
    },
    {
      icon: FiCoffee,
      title: 'Coffee Shop Ministry',
      description: 'Meeting students in campus coffee shops for one-on-one conversations and relationship building.',
      frequency: 'Weekly at each campus'
    },
    {
      icon: FiBook,
      title: 'Bible Studies',
      description: 'Small group Bible studies designed for seekers and new believers to explore faith together.',
      frequency: 'Weekly evening groups'
    },
    {
      icon: FiHeart,
      title: 'Prayer Walks',
      description: 'Walking through campus praying for students, faculty, and opportunities to share God\'s love.',
      frequency: 'Weekly prayer initiatives'
    }
  ];

  const campuses = [
    {
      name: 'University of Florida',
      location: 'Gainesville',
      students: '52,000+',
      team: '8 volunteers',
      established: '2018'
    },
    {
      name: 'Florida State University',
      location: 'Tallahassee',
      students: '41,000+',
      team: '6 volunteers',
      established: '2019'
    },
    {
      name: 'Florida International University',
      location: 'Miami',
      students: '58,000+',
      team: '5 volunteers',
      established: '2020'
    },
    {
      name: 'University of Central Florida',
      location: 'Orlando',
      students: '68,000+',
      team: '7 volunteers',
      established: '2020'
    },
    {
      name: 'Florida Institute of Technology',
      location: 'Melbourne',
      students: '6,000+',
      team: '3 volunteers',
      established: '2021'
    },
    {
      name: 'Florida Atlantic University',
      location: 'Boca Raton',
      students: '30,000+',
      team: '4 volunteers',
      established: '2022'
    }
  ];

  const testimonies = [
    {
      name: 'Alex M.',
      school: 'University of Florida',
      quote: 'I was walking to class when someone from Love 180 started a conversation with me. That simple interaction led to the most important decision of my life.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    },
    {
      name: 'Maria S.',
      school: 'Florida State University',
      quote: 'The Bible study changed everything for me. I went from questioning everything to having unshakeable faith in Jesus.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    },
    {
      name: 'David L.',
      school: 'University of Central Florida',
      quote: 'I thought college was just about getting a degree. Love 180 showed me it could also be about finding my purpose in Christ.',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    }
  ];

  return (
    <div>
      <Hero
        title="Campus Outreach"
        description="Meeting students where they are with authentic conversations, prayer, and community. We believe college campuses are mission fields filled with hearts ready for transformation."
        primaryCta={{
          text: 'Join Our Team',
          href: '/get-involved'
        }}
        secondaryCta={{
          text: 'View Events',
          href: '/events'
        }}
        backgroundImage="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Campus Activities */}
      <Section>
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            How We Reach Students
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Our approach is relational, authentic, and student-focused. We meet students in their everyday environments with genuine care and the hope of the gospel.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {activities.map((activity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={activity.icon} className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{activity.title}</h3>
                    <p className="text-neutral-600 mb-3">{activity.description}</p>
                    <div className="flex items-center space-x-2 text-sm text-primary-600">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                      <span>{activity.frequency}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Campus Locations */}
      <Section background="gray">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Where We Serve
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Love 180 has active outreach teams on campuses throughout Florida, with plans to expand to even more universities.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campuses.map((campus, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-semibold">{campus.name}</h3>
                </div>
                <div className="space-y-2 text-sm text-neutral-600">
                  <div>📍 {campus.location}</div>
                  <div>👥 {campus.students} students</div>
                  <div>🙋‍♂️ {campus.team}</div>
                  <div>📅 Est. {campus.established}</div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-neutral-600 mb-6">Don't see your campus? We'd love to start an outreach there!</p>
          <Button variant="primary" size="lg">
            Request Campus Outreach
          </Button>
        </div>
      </Section>

      {/* Student Testimonies */}
      <Section>
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Student Stories
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Hear from students whose lives have been transformed through campus outreach encounters.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonies.map((testimony, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full">
                <div className="flex items-center mb-4">
                  <img
                    src={testimony.image}
                    alt={testimony.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-neutral-900">{testimony.name}</h4>
                    <p className="text-sm text-neutral-600">{testimony.school}</p>
                  </div>
                </div>
                <p className="text-neutral-700 italic">"{testimony.quote}"</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Weekly Schedule Preview */}
      <Section background="gray">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
              Weekly Campus Schedule
            </h2>
            <p className="text-lg text-neutral-600 mb-6">
              Our teams maintain a consistent presence on campus throughout the week, creating multiple opportunities for students to encounter the love of Christ.
            </p>
            
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Monday - Friday</span>
                  <span className="text-primary-600">11:30 AM - 1:30 PM</span>
                </div>
                <p className="text-sm text-neutral-600 mt-1">Campus conversations during lunch rush</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Tuesday & Thursday</span>
                  <span className="text-primary-600">7:00 PM - 8:30 PM</span>
                </div>
                <p className="text-sm text-neutral-600 mt-1">Bible study groups</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Saturday</span>
                  <span className="text-primary-600">10:00 AM - 12:00 PM</span>
                </div>
                <p className="text-sm text-neutral-600 mt-1">Campus prayer walks</p>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
              alt="Students on campus"
              className="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </Section>
    </div>
  );
};

export default CampusOutreach;